import { useCallback, useState } from 'react';
import type { DragEvent as ReactDragEvent } from 'react';

import { useDropzone } from 'react-dropzone';
import type { DropzoneRootProps } from 'react-dropzone';

export type FileDragProps = DropzoneRootProps;

interface UseFileDragStateParams {
  onDropFiles: (files: File[]) => void;
  disabled?: boolean;
  onDropError?: (error: Error) => void;
}

/**
 * File drag-and-drop state for a dropzone, backed by react-dropzone. The
 * drag-state bookkeeping (balanced enter/leave tracking across child
 * elements, resetting when the drop lands on another dropzone or outside the
 * window, preventing browser navigation on missed drops) is full of event-
 * ordering pitfalls, so it is delegated to the library instead of hand-rolled.
 *
 * File extraction is react-dropzone's as well: its default getFilesFromEvent
 * (file-selector's fromEvent) starts the webkitGetAsEntry directory traversal
 * synchronously while the drop event is live, flattens dropped folders into a
 * single File list (relative paths kept on each file's `path`), and filters
 * out `.DS_Store`/`Thumbs.db`. Extraction failures surface via onDropError.
 *
 * Spread `dragProps` onto the dropzone element. It includes react-dropzone's
 * root ref, which the library needs to tell drops on this dropzone apart from
 * drops elsewhere — if the element needs its own ref too, compose them and
 * keep `rootRef` populated.
 */
export function useFileDragState(params: UseFileDragStateParams) {
  const { disabled = false, onDropError, onDropFiles } = params;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) onDropFiles(acceptedFiles);
    },
    [onDropFiles]
  );

  const { getRootProps, isDragActive, rootRef } = useDropzone({
    disabled,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onError: onDropError,
  });

  /**
   * dragover fallback for children that swallow dragenter: MUI X DataGrid
   * cells call stopPropagation() on every dragenter (see mui/mui-x#2680), so
   * react-dropzone's isDragActive — which is set on dragenter of the root —
   * never activates when a file drag enters through the grid. dragover still
   * bubbles (the grid only stops it during an active column reorder, which is
   * not a file drag), so it flips this supplementary flag. dragleave past the
   * root edge and drop clear it; both bubble unhindered.
   */
  const [isDragOverFallback, setIsDragOverFallback] = useState(false);

  const dragProps = getRootProps({
    onDragLeave: (e: ReactDragEvent<HTMLElement>) => {
      const related = e.relatedTarget;
      if (!(related instanceof Node) || !e.currentTarget.contains(related))
        setIsDragOverFallback(false);
    },
    onDragOver: (e: ReactDragEvent<HTMLElement>) => {
      if (getIsFileDrag(e.dataTransfer)) setIsDragOverFallback(true);
    },
    onDrop: () => setIsDragOverFallback(false),
  });

  return {
    dragProps,
    isDragging: !disabled && (isDragActive || isDragOverFallback),
    rootRef,
  };
}

function getIsFileDrag(dataTransfer: DataTransfer | null): boolean {
  return Boolean(dataTransfer?.types?.includes('Files'));
}
